import React, { useState, useEffect, useMemo } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import ActivityStreamItem, { ActivityChangeType } from "./ActivityStreamItem";
import { useCanvasId } from "@/utils/utils";
import useActivityStore, { ActivityItem } from "@/store/zustand/activityStore";

interface Props {
  selectActivity: (activity: any) => void;
  selectedActivityId: string;
}

const ActivityStream: React.FC<Props> = (props) => {
  const canvasId = useCanvasId();

  const activityStreamList = useActivityStore(
    (state) => state.activityStreamList
  );

  const restoringActivityId = useActivityStore(
    (state) => state.restoringActivityId
  );

  const [hasMore, setHasMore] = useState(true);
  const [infScrollerItems, setInfScrollerItems] = useState<ActivityItem[]>([]);
  const [idx, setIdx] = useState(0);

  // reset the scroll history if a new piece of data is inserted to activity stream
  useEffect(() => {
    let maxIndex = Math.max(20, idx);
    if (restoringActivityId) {
      maxIndex = Math.max(
        activityStreamList.findIndex(
          (activity) => activity.id === restoringActivityId
        ),
        maxIndex
      );
    }

    setInfScrollerItems(activityStreamList.slice(0, maxIndex));
    setHasMore(activityStreamList.length >= maxIndex);
    setIdx(Math.min(activityStreamList.length, maxIndex));
  }, [activityStreamList, canvasId, restoringActivityId]);

  const handleScrollerLoad = async () => {
    const remaining = activityStreamList.length - idx;
    setInfScrollerItems((infScrollerItems) =>
      infScrollerItems.concat(
        activityStreamList.slice(idx, idx + Math.min(20, remaining))
      )
    );
    setHasMore(idx + Math.min(20, remaining) < activityStreamList.length);
    setIdx(idx + Math.min(20, remaining));
  };

  const { currentActivity } = useMemo(() => {
    return {
      currentActivity: activityStreamList.find(
        (activity) => !activity.change_undone
      ),
    };
  }, [activityStreamList]);

  const activityStream = (
    <div className="flex flex-col items-center grow min-h-0">
      <div
        className="w-full h-full overflow-y-auto hide-scrollbar"
        id="scrollableDiv"
      >
        <InfiniteScroll
          className="flex flex-col text-left w-full divide-y divide-secondary-300"
          dataLength={infScrollerItems.length} // for some reason changing this to anything except the item length breaks it
          next={handleScrollerLoad}
          hasMore={hasMore}
          loader={
            <h4 className="text-center text-2xs text-secondary-800 py-2">
              Loading...
            </h4>
          }
          scrollableTarget="scrollableDiv"
          scrollThreshold={0.85}
          endMessage={
            <div className="text-center text-2xs text-secondary-800 py-2">
              {activityStreamList.length > 20 && "End of history"}
            </div>
          }
        >
          {infScrollerItems.map((item) => (
            <div key={item.id}>
              <ActivityStreamItem
                selected={item.id === props.selectedActivityId}
                timestamp={item.timestamp}
                changeUndone={item.change_undone}
                changeType={item.change_type as ActivityChangeType}
                isCurrentActivity={item.id === currentActivity?.id}
                activityId={item.id}
                onSelect={() => {
                  if (item.id === props.selectedActivityId) {
                    props.selectActivity(null);
                    return;
                  } else {
                    props.selectActivity(item);
                  }
                }}
              />
            </div>
          ))}
        </InfiniteScroll>
      </div>
    </div>
  );

  return activityStream;
};

export default ActivityStream;
